import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import { Job } from '../../types/job';
import JobDetailsCard from './JobDetailsCard';
import { useProfileStore } from '../../stores/profileStore';
import { useQuoteStore } from '../../stores/quoteStore';

import ReportButtonMenu, { BottomSuccessMessage } from '../reports/ReportButtonMenu';
import ReportModal from '../reports/ReportModal';
import { useProfileRole } from '../../hooks/useProfileRole';
import { useTranslation } from 'react-i18next';

interface JobDetailsModalProps {
  job: Job;
  onClose: () => void;
  onApply?: () => void;
  onClick?: () => void;
}

const JobDetailsModal: React.FC<JobDetailsModalProps> = ({ job, onClose, onApply, onClick }) => {
  const { profile } = useProfileStore();
  const { fetchQuoteIdByJobId } = useQuoteStore();
  const { t } = useTranslation(['jobs', 'reports', 'admin']);
  const { isAdmin, isFreelancer } = useProfileRole();

  const [copied, setCopied] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [isFetchingQuote, setIsFetchingQuote] = useState(false);

  useEffect(() => {
    const getQuoteId = async () => {
      if (job.has_user_quote) {
        setIsFetchingQuote(true);
        try {
          const id = await fetchQuoteIdByJobId(job.id);
          if (id) {
            setQuoteId(id);
          }
        } catch (error) {
          console.error('Error fetching quote ID:', error);
        } finally {
          setIsFetchingQuote(false);
        }
      }
    };

    getQuoteId();
  }, [job.id, job.has_user_quote, fetchQuoteIdByJobId]);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isOwner = profile?.id === job.client.id;
  const hasUserQuote = job.has_user_quote;

  return (
    <>
      <FullBlockingModal innerClassName="p-0 md:p-0 md:max-w-[60rem] rounded-[1.75rem] border border-secondary/[0.06] shadow-[0_40px_80px_-32px_rgba(13,45,57,0.35),0_2px_8px_rgba(13,45,57,0.06)]">
        <div className="absolute right-3 top-3 sm:right-5 sm:top-5 md:right-8 md:top-8">
          <ReportButtonMenu
            onReport={() => setShowReportModal(true)}
            entityOwner={job.client.id}
            onCopySuccess={handleCopySuccess}
            contentTypeTitle="report_this_offer"
          />
        </div>

        <JobDetailsCard job={job} />

        {!isOwner && hasUserQuote && !isAdmin && (
          <div className="px-5 sm:px-8 md:px-11">
            <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle size={20} className="text-yellow-500" aria-hidden />
                </div>
                <div className="ml-3 flex flex-col justify-between gap-2 md:flex-row md:items-center md:gap-4">
                  <p className="text-sm text-yellow-800">
                    {t('already_submitted_warning', {
                      ns: 'jobs',
                      defaultValue: 'Vous avez déjà créé une soumission pour ce projet.',
                    })}
                  </p>
                  <Button
                    className="rounded-full px-4 py-1 text-sm font-semibold md:text-sm"
                    variant="primary"
                    href={quoteId ? `/projects/${job.id}?selectedQuoteId=${quoteId}` : undefined}
                    // Supprimez la ligne loading={isFetchingQuote}
                    disabled={!quoteId && !isFetchingQuote}
                  >
                    {t('go_to_my_quote', { ns: 'jobs' })}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col justify-end gap-6 border-t border-secondary/10 px-5 pb-6 pt-6 sm:px-8 md:flex-row md:items-center md:gap-8 md:px-11 md:pb-9 md:pt-7">
          <Button
            className="w-auto px-4 py-2 font-semibold text-secondary no-underline hover:underline"
            onClick={onClose}
            type="button"
            variant="simple-text"
          >
            {t('close_and_back', { ns: 'jobs' })}
          </Button>

          {/* Freelancer-only actions: Apply and Go to my quote. Apply is gated on isFreelancer
              explicitly so recruiters never see it, even for a job they own. No job-level
              actions (edit/publish/cancel) for freelancers. */}
          {isFreelancer && !isOwner && !hasUserQuote && (
            <Button
              className="w-full rounded-full bg-gradient-to-br from-primary to-primary-over px-8 py-2 font-bold shadow-[0_14px_28px_-12px_rgba(0,89,89,0.5)] transition-transform hover:-translate-y-px md:mt-0 md:w-auto"
              variant="primary"
              onClick={() => {
                onApply?.();
              }}
              type="button"
            >
              <span className="inline-flex items-center gap-2">
                {t('apply_for_offer', { ns: 'jobs' })}
                <ArrowRight size={18} aria-hidden />
              </span>
            </Button>
          )}

          {isAdmin && (
            <Button
              className="w-full rounded-full px-8 py-2 font-bold md:mt-0 md:w-auto"
              variant="primary"
              onClick={() => {
                onClick?.();
              }}
              type="button"
            >
              {t('cancel_this_job', { ns: 'admin' })}
            </Button>
          )}
        </div>
      </FullBlockingModal>

      {showReportModal && job && (
        <ReportModal
          entityId={job.id}
          reportedId={job.client.id}
          contentType="job"
          onClose={() => setShowReportModal(false)}
        />
      )}

      {copied && <BottomSuccessMessage />}
    </>
  );
};

export default JobDetailsModal;
